import React, { useState } from 'react';
import {
  Box, Button, Paper, Stack, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend, LineChart, Line, Brush, LabelList,
} from 'recharts';
import { day } from './ScopeFilters';
import { inWords } from './stageWords';
import DownloadTable from '../shell/DownloadTable';

// The share of a vehicle's fixes that broke the limit.
//
// The server records how many fixes were WITHIN the limit, because that is the cheaper thing to
// count; the panel is about the ones that were not. Written once and used by both series, because
// the two were computed separately and a panel titled "exceptions" spent a release plotting
// compliance - every bar at 98%, which reads as a fault in the data rather than a well-run fleet.
export const overLimitPercent = (samples, within) => (samples
  ? Math.round((100 * (samples - within)) / samples)
  : null);

// The worst single reading so far, as the hours are added up.
//
// Kept beside the percentage because it is the thing a speeding report is actually read for: a
// share says how often, this says how badly. A lorry that touched seventy once is a different
// conversation from one that sat at forty-two all week, and the share alone cannot tell them apart.
//
// Tolerant of an hour that recorded no maximum - an hour with no driving in it has none - because
// Math.max of anything and undefined is NaN, and a NaN on a dashboard is worse than a blank.
export const fasterOf = (held, count) => Math.max(held || 0, Math.round(count?.maxKph || 0));

/**
 * The rows that have something to show.
 *
 * A chart about things that went wrong should not draw a bar for a thing that did not. A panel
 * headed "exceptions" was drawing a column for every vehicle that drove, so a fleet with three
 * offenders showed three bars and seven labelled zeroes - which reads as a chart that failed to
 * load, not as seven lorries driving properly. The same went for severities with nothing
 * outstanding in them.
 *
 * This is only right where a zero means "not one of these". Where the categories themselves are the
 * point - the phases a trip can be in, the states a tyre can be in, the health a reader can be in -
 * an empty one is information and belongs on the axis.
 */
export const nonZero = (rows, key) => (rows || []).filter((row) => row[key] > 0);

/** The vehicles that actually exceeded the limit. The rest of the fleet is in the spreadsheet. */
export const exceededOnly = (rows) => nonZero(rows, 'percentage');

export const turnaroundTime = (minutes) => {
  if (minutes == null || !Number.isFinite(Number(minutes))) return '—';
  const total = Math.max(0, Math.round(Number(minutes)));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};
const tripDateTime = (value) => new Date(value).toLocaleString(undefined, {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

// Series colour comes from the product's cool family rather than from fixed hexes. Two reasons the
// fixed ones were wrong: the brand navy is near-black, so a bar painted with it disappeared against
// the dark mode's ground; and amber and red are spent entirely on operational status, so a category
// must never borrow them. Order follows the same progression as the duration ramp.
export const chartColors = (theme) => [
  theme.palette.accents.teal,
  theme.palette.accents.blue,
  theme.palette.accents.indigo,
  theme.palette.accents.violet,
  theme.palette.accents.neutral,
];
const label = inWords;

const Panel = ({
  title, note, children, empty, reason, action,
}) => (
  <Paper variant="outlined" sx={{ p: 2.5, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
      <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>{title}</Typography>
      {action}
    </Box>
    <Typography variant="caption" color="text.secondary">{note}</Typography>
    <Box sx={{ height: 240, mt: 2 }}>{empty ? <Typography color="text.secondary" sx={{ pt: 8, textAlign: 'center', px: 2 }}>{reason || 'No data for this selection'}</Typography> : children}</Box>
  </Paper>
);
// Which half of a counted hour each bar reads.
const phases = { IN_PLANT: 'plant', IN_TRANSIT: 'road' };
const nothing = { samples: 0, within: 0, maxKph: 0 };

const PerformanceCharts = ({
  trips, issues = [], config = {}, speed: hours = [],
}) => {
  const colors = chartColors(useTheme());
  // Dragging the handles under the trend picks a window; narrow it to a few days and the chart
  // stops averaging and shows the trips themselves. A mean over three days hides the two trucks
  // that took four hours, which is the only thing anybody opens this chart to find.
  const [window, setWindow] = useState(null);
  // Clicking a severity asks what those issues actually were.
  const [severityFocus, setSeverityFocus] = useState('');
  // Clicking a phase asks where those trucks actually are; clicking a speed bar asks who is
  // responsible for the percentage.
  const [phaseFocus, setPhaseFocus] = useState('');
  const [speedFocus, setSpeedFocus] = useState('');
  // Only the phases that have trips in them. Five columns of which two are permanently zero is what
  // this chart looked like on a fleet that never passes a gate - a captive truck is never "in plant"
  // or "in parking", so those columns could only ever say nothing. Gate traffic populates them and
  // is unaffected; the chart simply fits the traffic it is drawn for.
  const split = nonZero(
    ['IN_PLANT', 'IN_TRANSIT', 'PLANNED', 'AT_YARD', 'CLOSED'].map((phase, i) => ({
      phase,
      name: ['In plant', 'In transit', 'Planned', 'In parking', 'Closed'][i],
      value: trips.filter((t) => t.phase === phase).length,
    })),
    'value',
  );
  const dates = [...new Set(trips.map((t) => day(t.createdAt)))].sort();
  const trend = dates.map((date) => {
    const rows = trips.filter((t) => day(t.createdAt) === date && t.gateIn && t.gateOut);
    return { date, minutes: rows.length ? Math.round(rows.reduce((sum, t) => sum + (Date.parse(t.gateOut) - Date.parse(t.gateIn)) / 60000, 0) / rows.length) : null };
  });
  const unresolved = issues.filter((i) => i.status !== 'RESOLVED');
  // Only the severities that have something outstanding in them: "CRITICAL 0" beside "HIGH 0" is
  // four empty columns where the chart should simply say there is nothing to answer.
  const severity = nonZero(
    ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((s) => ({ name: s, count: unresolved.filter((i) => i.severity === s).length })),
    'count',
  );
  // The same issues broken out by what actually went wrong, once a severity has been chosen.
  const byType = severityFocus ? Object.entries(issues
    .filter((i) => i.severity === severityFocus && i.status !== 'RESOLVED')
    .reduce((counts, i) => ({ ...counts, [i.type || 'OTHER']: (counts[i.type || 'OTHER'] || 0) + 1 }), {}))
    .map(([name, count]) => ({ name: label(name), count }))
    .sort((a, b) => b.count - a.count) : [];

  // A phase opened up: where its trucks stand now, or - once a trip is finished and every stage
  // reads the same - why it ended. Each is the only breakdown that says anything for that phase.
  const inPhase = trips.filter((t) => t.phase === phaseFocus);
  const byStage = phaseFocus ? Object.entries(inPhase
    .reduce((counts, t) => {
      const key = phaseFocus === 'CLOSED' ? (t.closureReason || 'UNKNOWN') : (t.stage || 'UNKNOWN');
      return { ...counts, [key]: (counts[key] || 0) + 1 };
    }, {}))
    .map(([name, count]) => ({ name: label(name), count }))
    .sort((a, b) => b.count - a.count) : [];

  // Compliance is an average, and an average never names the vehicle dragging it down. The hours
  // already hold the counts per vehicle, so opening a bar is addition rather than another request.
  const worst = new Map();
  if (speedFocus) {
    hours.forEach((hour) => (hour.vehicles || []).forEach((v) => {
      const count = v[phases[speedFocus]] || nothing;
      if (!count.samples) return;
      const row = worst.get(v.vehicle) || { name: v.vehicle, within: 0, total: 0, fastest: 0 };
      worst.set(v.vehicle, {
        name: row.name,
        within: row.within + count.within,
        total: row.total + count.samples,
        // The worst single reading of the period, not an average of them: a lorry that touched
        // seventy once is a different conversation from one that sat at forty-two all week.
        fastest: fasterOf(row.fastest, count),
      });
    }));
  }
  // Every vehicle that drove, worst first - and then only as many bars as can still be read.
  //
  // A yard can run hundreds of vehicles. Two hundred bars on one axis is a grey smear with no
  // legible labels, so the chart shows the end of the list that anybody acts on. What was missing is
  // that it never said so: a reader with two hundred vehicles saw ten bars and had no way to know
  // the other hundred and ninety existed. So the count is stated, and the whole list is one click
  // away as a spreadsheet rather than something only the chart can reach.
  const speedByVehicleAll = [...worst.values()]
    .map((row) => ({
      name: row.name,
      percentage: overLimitPercent(row.total, row.within),
      fastest: row.fastest,
      samples: row.total,
    }))
    // Most exceptions first, which is now the top of the scale rather than the bottom.
    .sort((a, b) => b.percentage - a.percentage);
  const SHOWN = 10;
  // Only the ones with something to show. The full list, clean lorries included, is a click away as
  // a spreadsheet.
  const speedOffenders = exceededOnly(speedByVehicleAll);
  const speedByVehicle = speedOffenders.slice(0, SHOWN);

  // The days the window covers, and - when that is a handful - the individual trips inside it.
  const picked = window ? trend.slice(window.start, window.end + 1).map((t) => t.date) : [];
  const zoomed = picked.length > 0 && picked.length <= 3;
  const detail = zoomed ? trips
    .filter((t) => picked.includes(day(t.createdAt)) && t.gateIn && t.gateOut)
    .map((t) => ({
      name: t.vehicle,
      minutes: Math.round((Date.parse(t.gateOut) - Date.parse(t.gateIn)) / 60000),
      at: Date.parse(t.gateIn),
      challan: t.challanNumber || t.shipmentId,
    }))
    .sort((a, b) => a.at - b.at) : [];
  // Hours counted by the server, added up: a week of driving rather than whatever fits in one
  // response. An hour that was never counted is absent, not zero.
  const speed = [['IN_PLANT', 'Plant'], ['IN_TRANSIT', 'Road']].map(([phase, name]) => {
    const total = hours.reduce((sum, hour) => (hour.vehicles || []).reduce((acc, v) => {
      const count = v[phases[phase]] || nothing;
      return {
        samples: acc.samples + count.samples,
        within: acc.within + count.within,
        fastest: fasterOf(acc.fastest, count),
      };
    }, sum), { samples: 0, within: 0, fastest: 0 });
    // The share of the driving OVER the limit. Counted as the complement of the readings within
    // it, because "within" is what the server records; a reading is one or the other. The count
    // itself stays off the screen: it is how the percentage is worked out, not something anybody
    // can act on - twice as many readings is a unit reporting twice as often, not a lorry driving
    // twice as far.
    return {
      phase,
      name,
      percentage: overLimitPercent(total.samples, total.within),
      fastest: total.fastest,
      samples: total.samples,
    };
  });
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, my: 3 }}>
      <Panel
        title={phaseFocus ? `Trip distribution · ${label(phaseFocus)}` : 'Trip distribution'}
        note={phaseFocus
          ? (phaseFocus === 'CLOSED' ? 'How these trips ended' : 'The stage these trucks have reached')
          : 'Current phase of trips created in the selected period. Select a slice to see where those trucks are.'}
        empty={!trips.length}
        action={phaseFocus && <Button size="small" onClick={() => setPhaseFocus('')}>All phases</Button>}
      >
        <ResponsiveContainer>
          {phaseFocus ? (
            <BarChart data={byStage}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={62} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Trips" fill={colors[0]} radius={[5, 5, 0, 0]} />
            </BarChart>
          ) : (
            <PieChart>
              <Pie
                data={split.filter((s) => s.value)}
                dataKey="value"
                nameKey="name"
                innerRadius={55}
                outerRadius={85}
                cursor="pointer"
                onClick={(slice) => slice?.payload?.phase && setPhaseFocus(slice.payload.phase)}
              >
                {split.filter((s) => s.value).map((s, i) => <Cell key={s.name} fill={colors[i]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          )}
        </ResponsiveContainer>
      </Panel>
      <Panel
        title="Turnaround trend"
        note={zoomed
          ? `Every trip that entered on ${picked[0]}${picked.length > 1 ? ` to ${picked[picked.length - 1]}` : ''}, in Gate In order`
          : 'Mean Gate In → Gate Out as hours:minutes, by trip creation day. Drag the handles below to narrow to three days or fewer and the trips themselves appear.'}
        empty={!trend.some((t) => t.minutes != null)}
        action={window && <Button size="small" onClick={() => setWindow(null)}>Reset zoom</Button>}
      >
        <ResponsiveContainer>
          {zoomed && detail.length ? (
            <BarChart data={detail}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={54} />
              <YAxis tickFormatter={turnaroundTime} />
              <Tooltip labelFormatter={(name, payload) => `${name} · ${tripDateTime(payload?.[0]?.payload?.at)}`} formatter={(value) => [turnaroundTime(value), 'Turnaround (h:mm)']} />
              <Bar dataKey="minutes" name="Minutes" fill={colors[0]} radius={[5, 5, 0, 0]} />
            </BarChart>
          ) : (
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={turnaroundTime} />
              <Tooltip formatter={(value) => [turnaroundTime(value), 'Mean turnaround (h:mm)']} />
              <Line name="Turnaround" type="monotone" dataKey="minutes" stroke={colors[0]} strokeWidth={3} connectNulls={false} />
              {trend.length > 3 && (
                <Brush
                  dataKey="date"
                  height={18}
                  travellerWidth={8}
                  stroke={colors[0]}
                  onChange={(range) => setWindow(range && range.startIndex != null ? { start: range.startIndex, end: range.endIndex } : null)}
                />
              )}
            </LineChart>
          )}
        </ResponsiveContainer>
      </Panel>
      <Panel
        title={severityFocus ? `ZENY severity · ${label(severityFocus)}` : 'ZENY severity'}
        note={severityFocus ? 'What these issues were. Select another severity from the chart behind this one.' : 'Unresolved issues in the selected scope. Select a bar to see what they were.'}
        empty={!unresolved.length}
        reason="Nothing is outstanding in this scope."
        action={severityFocus && <Button size="small" onClick={() => setSeverityFocus('')}>All severities</Button>}
      >
        <ResponsiveContainer>
          <BarChart data={severityFocus ? byType : severity} onClick={(e) => !severityFocus && e?.activeLabel && setSeverityFocus(e.activeLabel)}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={severityFocus ? -25 : 0} textAnchor={severityFocus ? 'end' : 'middle'} height={severityFocus ? 62 : 30} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="count" name="Issues" fill={colors[0]} radius={[5, 5, 0, 0]} cursor={severityFocus ? 'default' : 'pointer'} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>
      <Panel
        title={speedFocus ? `Fleet speed exceptions · ${speedFocus === 'IN_PLANT' ? 'Plant' : 'Road'}` : 'Fleet speed exceptions'}
        note={speedFocus
          ? `${speedOffenders.length > SHOWN ? `${SHOWN} of ${speedOffenders.length}` : speedOffenders.length} vehicle${speedOffenders.length === 1 ? '' : 's'} exceeded the limit, worst first, by how much of its own driving was over it — of ${speedByVehicleAll.length} that drove`
          : 'All authorized vehicles · Last seven days · Independent of the trip filters above. Select a bar to see which vehicles.'}
        // Three different nothings, and they mean opposite things.
        //
        // No limit configured and no driving counted are both "we cannot tell you". A fleet that
        // drove and broke the limit nowhere is the best possible answer - but as a chart it is a row
        // of zero-height bars, which looks exactly like a broken panel. Since this counts exceptions
        // rather than compliance, nothing to draw is now said in words instead of drawn as nothing.
        empty={!speed.some((s) => s.percentage != null) || !speed.some((s) => s.percentage > 0)}
        reason={(() => {
          if (!config.plantSpeedKph && !config.roadSpeedKph) return 'No speed limit is configured. Set plant and road limits under Configuration → Operational rules.';
          if (!speed.some((s) => s.percentage != null)) return 'No driving has been counted yet. The first full hour is counted at the end of it.';
          const quickest = Math.max(...speed.map((s) => s.fastest || 0));
          const limit = speedFocus === 'IN_PLANT' ? config.plantSpeedKph : config.roadSpeedKph;
          return quickest > 0
            ? `No vehicle exceeded the limit in this period. The fastest was ${quickest} km/h${limit ? ` against a limit of ${limit}` : ''}.`
            : 'No vehicle exceeded the limit in this period.';
        })()}
        action={speedFocus && (
          <Stack direction="row" gap={1}>
            <DownloadTable
              name="speed-exceptions"
              label="All vehicles"
              rows={speedByVehicleAll}
              columns={[
                ['Vehicle', (row) => row.name],
                ['Over limit (%)', (row) => row.percentage],
                ['Fastest (km/h)', (row) => row.fastest],
              ]}
            />
            <Button size="small" onClick={() => setSpeedFocus('')}>Both</Button>
          </Stack>
        )}
      >
        <ResponsiveContainer>
          <BarChart
            data={speedFocus ? speedByVehicle : speed}
            onClick={(e) => {
              if (speedFocus) return;
              const picked = speed.find((s) => s.name === e?.activeLabel);
              if (picked?.percentage != null) setSpeedFocus(picked.phase);
            }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: speedFocus ? 10 : 12 }} interval={0} angle={speedFocus ? -25 : 0} textAnchor={speedFocus ? 'end' : 'middle'} height={speedFocus ? 62 : 30} />
            {/* Exceptions are a small number when things are going well, and a fixed 0-100 axis
                draws a well-run fleet as a row of invisible stubs - which is how a chart that is
                working gets read as a chart that is broken. The scale follows the data instead, with
                a floor so a near-perfect week is not magnified into drama, and every bar carries its
                own figure so the height is never the only thing saying how bad it is. */}
            <YAxis domain={[0, (max) => Math.max(5, Math.ceil(max * 1.25))]} unit="%" allowDecimals={false} />
            <Tooltip formatter={(value, key, item) => [
              `${value}%`,
              item?.payload?.fastest ? `of driving over the limit · fastest ${item.payload.fastest} km/h` : 'of driving over the limit',
            ]}
            />
            <Bar dataKey="percentage" name="Over limit (%)" fill={colors[1]} radius={[5, 5, 0, 0]} cursor={speedFocus ? 'default' : 'pointer'}>
              <LabelList dataKey="percentage" position="top" formatter={(v) => (v == null ? '' : `${v}%`)} style={{ fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Panel>
    </Box>
  );
};
export default PerformanceCharts;
